import { useMutation } from '@apollo/client';
import type { InsertImpactOversightRatingMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertImpactOversightRatingDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertImpactOversightRatingInput } from './useInsertImpactOversightRatingTRPC';
import { useInsertImpactOversightRatingTRPC } from './useInsertImpactOversightRatingTRPC';

export const useInsertImpactOversightRating = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertImpactOversightRatingDocument,
    {
      update: (cache) => {
        evictField(cache, 'impact_oversight_rating');
        evictField(cache, 'oversight_assessment');
      },
    }
  );

  const trpcMutation = useInsertImpactOversightRatingTRPC();

  const insertImpactOversightRating = async (
    variables: InsertImpactOversightRatingInput
  ): Promise<InsertImpactOversightRatingMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertImpactOversightRating(variables);
    }

    const result = await insertGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert impact oversight rating');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertImpactOversightRating,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertImpactOversightRating,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
